import React, { useState, useMemo } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { Column, Row, Expaned } from '@/components/layout/layout'
import { getBEMCls } from '@/utils/classname'

import './purchase-detail.scss'
import FormTitle from '../../../../components/form/form-title/form-title'
import { IPurchaseOrderList, IPurchaseOrderInfo } from './type'
import Table, { ColumnsType } from 'antd/lib/table'
import { useRequest, usePersistFn } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { Spin } from 'antd'
import Button from '@/components/button/button'
import { Modal } from '@/components/dialog/modal'
import { purchaseOrderStore } from '../../model'
import OrderHookImg from '@/assets/images/order-hook.svg'
import Iconfont from '@/components/iconfont/iconfont'
import OrderInInfo from './order-in'
import EditPurchase from '../edit-purchase/edit-purchase'
import { staffStore } from '@/store/staff'

interface PurchaseDetailProps {
    orderId: number
}

const PurchaseDetail: React.FC<PurchaseDetailProps> = props => {
    const hasCostPermission = !!staffStore.canIUse(39)
    const cls = (...arg: string[]) => getBEMCls('purchase-detail', ...arg)

    const [orderInfo, setOrderInfo] = useState<IPurchaseOrderInfo>()

    const { loading } = useRequest(
        () =>
            $post({
                url: '/warehouse/getpurchaseorderdetail',
                params: {
                    order_id: props.orderId,
                },
            }),
        {
            onSuccess: res => {
                setOrderInfo(res.order_info)
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const removeOrder = useRequest(
        () =>
            $post({
                url: '/warehouse/deletepurchaseorder',
                params: {
                    order_id: props.orderId,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '采购单已作废')
                layerManage.pop()
                purchaseOrderStore.setUpdateStatus()
            },
            onError: e => $msg('error', e.message),
        },
    )

    const handleRomoveOrder = usePersistFn(() => {
        showLayer(
            <Modal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '156px' }}
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    removeOrder.run()
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        确认作废该采购单吗？
                    </div>
                    <div data-font-size-14 data-color-999 data-margin-top-8>
                        作废后无法撤销
                    </div>
                </div>
            </Modal>,
        )
    })

    const column: ColumnsType<IPurchaseOrderList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品',
            key: 'goods',
            dataIndex: 'goods',
            ellipsis: true,
            render: val => val.name,
        },
        {
            title: '供货商',
            key: 'supplier',
            dataIndex: 'supplier',
            ellipsis: true,
            render: val => val.name,
        },
        {
            title: '单价',
            key: 'price',
            dataIndex: 'price',
            render: val => (hasCostPermission ? (val == null ? '-' : `¥${val}`) : '***'),
        },
        {
            title: '采购量',
            key: 'amount',
            dataIndex: 'amount',
            render: (val, record) => `${val}${record.purchase_unit.name}`,
        },
        {
            title: '小计',
            key: 'subtotal',
            dataIndex: 'subtotal',
            render: val => (hasCostPermission ? (val == null ? '-' : `¥${val}`) : '***'),
        },
    ]

    const orderStatus = useMemo(() => {
        if (orderInfo?.is_draft) {
            return <span className="danger-color">草稿</span>
        } else {
            switch (orderInfo?.stocked_state) {
                case 0:
                    return <span className="them-color">待入库</span>
                case 1:
                    return <span className="them-color">部分入库</span>
                case 2:
                    return <span>已入库</span>
            }
        }
    }, [orderInfo])

    return (
        <BaseDrawer
            onClose={() => {
                layerManage.pop()
            }}
            isShow
            width={640}
            title="采购单详情"
        >
            <Column className={cls()} data-align-items-center>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        <div className={cls('block')}>
                            <div className={cls('container')}>
                                <div className={cls('container', 'top')}>
                                    <Column data-expaned data-center-center>
                                        <div className="price">
                                            <div data-font-size-12>¥</div>
                                            <div data-line-height-20>
                                                {hasCostPermission ? orderInfo?.purchase_subtotal ?? '-' : '***'}
                                            </div>
                                        </div>
                                        <div className="des">采购金额</div>
                                    </Column>
                                    <Column data-expaned data-center-center>
                                        <div className="num">
                                            {orderInfo?.order_goods_list.reduce((pre, next) => pre + next.amount, 0)}
                                        </div>
                                        <div className="des">采购总量</div>
                                    </Column>
                                </div>
                                <div className={cls('container', 'bottom')}>
                                    <Column data-justify-content-center data-expaned>
                                        <div className={cls('list-item')}>
                                            <div className="title coll">采购时间：</div>
                                            <div className="txt">{orderInfo?.purchase_time}</div>
                                        </div>
                                        <div data-margin-top-12 className={cls('list-item')}>
                                            <div className="title coll">状态：</div>
                                            <div className="txt">{orderStatus}</div>
                                        </div>
                                    </Column>
                                    <Column data-justify-content-center data-expaned>
                                        <div className={cls('list-item')}>
                                            <div className="title coll">采购单号：</div>
                                            <div className="txt">{orderInfo?.order_no}</div>
                                        </div>
                                        <div data-margin-top-12 className={cls('list-item')}>
                                            <div className="title coll">操作人：</div>
                                            <Row className="txt" data-align-items-center>
                                                <img
                                                    data-margin-right-6
                                                    src={orderInfo?.creator.user.avatar}
                                                    alt=""
                                                    className="avatar"
                                                />
                                                <div data-width-126 className="text-popper">
                                                    {orderInfo?.creator.user.realname ||
                                                        orderInfo?.creator.user.nickname}
                                                </div>
                                            </Row>
                                        </div>
                                    </Column>
                                </div>
                            </div>
                            {!!orderInfo?.stocked_state && (
                                <div className={cls('container2')}>
                                    <img
                                        src={OrderHookImg}
                                        className={`${cls('order-hook', 'left')} ${cls('order-hook')}`}
                                        alt=""
                                    />
                                    <img
                                        src={OrderHookImg}
                                        className={`${cls('order-hook', 'right')} ${cls('order-hook')}`}
                                        alt=""
                                    />
                                    <div className={cls('list-item')}>
                                        <div className="title">实际入库单量：</div>
                                        <div className="txt">
                                            {orderInfo?.stock_in_order.reduce(
                                                (pre, next) => pre + next.stocked_in_amount,
                                                0,
                                            )}
                                        </div>
                                    </div>
                                    <div className={cls('list-item')}>
                                        <div className="title">关联入库单号：</div>
                                        <Row
                                            data-align-items-center
                                            data-cursor-pointer
                                            onClick={() => {
                                                showLayer(<OrderInInfo list={orderInfo?.stock_in_order} />)
                                            }}
                                        >
                                            <div className="txt order_no">
                                                {orderInfo?.stock_in_order.map(order => order.order_no).join('、')}
                                            </div>
                                            <Iconfont name="icon6youjiantou-20" size={14} />
                                        </Row>
                                    </div>
                                </div>
                            )}
                        </div>

                        <FormTitle title="采购商品列表" hasLine={false} />

                        <div className={cls('table')}>
                            <Table
                                rowKey={record => record.id}
                                bordered
                                pagination={false}
                                columns={
                                    hasCostPermission
                                        ? column
                                        : column.filter(item => item.key !== 'price' && item.key !== 'subtotal')
                                }
                                dataSource={orderInfo?.order_goods_list}
                                scroll={{ x: 'max-content', y: 272 }}
                            />
                        </div>
                    </Spin>
                </Expaned>
                {orderInfo?.stocked_state === 0 && (
                    <div className={cls('footer')}>
                        <Button
                            types="text"
                            style={{
                                fontWeight: 600,
                            }}
                            onClick={handleRomoveOrder}
                        >
                            作废
                        </Button>
                        <Button
                            style={{
                                marginLeft: '10px',
                            }}
                            types="primary"
                            onClick={() => {
                                layerManage.pop()
                                showLayer(<EditPurchase orderId={props.orderId} />)
                            }}
                        >
                            编辑
                        </Button>
                    </div>
                )}
            </Column>
        </BaseDrawer>
    )
}

export default PurchaseDetail
